import React, {PureComponent} from 'react';
import {withRouter} from 'react-router';
import {Switch} from 'react-router-dom';
import {hot} from 'react-hot-loader';
import {connect} from 'react-redux';
import loadable from '@loadable/component';

/* Routes */

import {renderRoutes} from '../routes/routesDesktop';

/* Constants */
import {DESKTOP_COMPONENTS, DESKTOP_COMPONENTS_OPTIONS} from '../modules/voltran/voltranConstants';
import {PAGES, PLATFORMS} from '../core/route/routeConstants';
import {LOCATION_COOKIE_KEY} from '../modules/delivery/deliveryConstants';
import {FEATURE_TOGGLE_NAMES} from '../modules/feature-toggle/featureToggleConstants';
import {FACET_QUERY_PARAM_NAMES} from '../views/filter/facet/facetConstants';
import {PAGES_WITH_SPECIAL_FOOTER} from '../modules/footer/footerConstants';

/* Actions */
import {actionCreators as voltranActionCreators} from '../redux/actions/voltranActions';
import {actionCreators as filterActionCreators} from '../redux/actions/filterActions';
import {actionCreators as featureToggleActionCreators} from '../redux/actions/featureToggleActions';
import {actionCreators as footerActionCreators} from '../redux/actions/footerActions';

/* Utils */
import {extractQueryParamsFromLocation, matchUrlInRouteConfigs} from '../core/route/routeUtils';
import {resolvePromiseList, resolveWithSettleContext} from '../common/async/promiseUtils';
import {getBrowserCookie} from '../../client/common/persistence/browserCookieUtils';
import {isExistPartialFragment} from '../modules/voltran/voltranUtils';
import {jsonParse} from '../utils/objectUtils';

/* Core */
import ReactRenderContext from '../core/react/ReactRenderContext';
import utagBootstrapper from '../../client/core/utag/utagBootstrapper';

/* Loadable Components */
const MasterLayout = loadable(() => import('../layouts/desktop/MasterLayout/MasterLayout'));

const APP_DESKTOP_FEATURE_TOGGLES = [
  FEATURE_TOGGLE_NAMES.DISPLAY_WHATSAPP_ICON,
  FEATURE_TOGGLE_NAMES.DISPLAY_TABBAR,
  FEATURE_TOGGLE_NAMES.DISPLAY_LIKE_BUTTON_ON_LISTING_PAGES,
  FEATURE_TOGGLE_NAMES.OPEN_TABBAR_CACHE,
  FEATURE_TOGGLE_NAMES.ENABLE_DEEP_LINK_QUESTIONING,
  FEATURE_TOGGLE_NAMES.ENABLE_FRIENDLY_URLS_FOR_CATEGORY_PAGES,
];

const excludedADPages = [
  PAGES.CATEGORY_PAGE,
  PAGES.COLLECTION_PAGE,
  PAGES.BRAND_CATEGORY_PAGE,
  PAGES.HURLY_CATEGORY_PAGE,
  PAGES.SEARCH_PAGE,
  PAGES.TAG_PAGE,
  PAGES.PRODUCT_DETAIL_PAGE_WITH_PRODUCT_SKU,
  PAGES.PRODUCT_DETAIL_PAGE_WITH_PRODUCT_ID,
  PAGES.PRODUCT_COMMENT_PAGE_WITH_PRODUCT_ID,
  PAGES.PRODUCT_COMMENT_PAGE_WITH_PRODUCT_SKU,
];

class AppDesktop extends PureComponent {
  static async fetchData(dispatch, context) {
    const {
      cookies,
      pathParams,
      rewriteUrl,
      isBot
    } = context;

    const {
      staticPageId,
    } = pathParams;

    let result;
    let fetchOperationContext = {
      isPartialContent: false,
    };

    dispatch({type: 'SET_REWRITE_URL', payload: rewriteUrl})

    const getFeatureTogglesResponse = await resolveWithSettleContext(dispatch(
      featureToggleActionCreators.getAppFeatureToggles.trigger(
        APP_DESKTOP_FEATURE_TOGGLES,
      ),
    ))
    if (getFeatureTogglesResponse.hasError) {
      fetchOperationContext.isError = getFeatureTogglesResponse.result;
      return fetchOperationContext;
    }

    const AdvertisementFragment = [{
      ...DESKTOP_COMPONENTS_OPTIONS[DESKTOP_COMPONENTS.ADVERTISEMENT],
      paramater: {
        targeting: staticPageId ? `staticPage:${staticPageId}` : null,
        preventHidingAd: true,
        ...context.matchResult.routeName === PAGES.HOME_PAGE ?
          {adUnitPath: '/21797185156/Desktop-Masthead/Desktop-Masthead-Anasayfa'} :
          {adUnitPath: '/21797185156/Desktop-Masthead/Desktop-Masthead-Diger'},
      },
    }]

    const enableAdvertisement = !excludedADPages.includes(context.matchResult.routeName);
    const hasSpecialFooter = PAGES_WITH_SPECIAL_FOOTER.includes(context.matchResult.routeName)

    let fragments = [
      ...enableAdvertisement ? AdvertisementFragment : [],
      DESKTOP_COMPONENTS_OPTIONS[DESKTOP_COMPONENTS.FAVORITE],
      DESKTOP_COMPONENTS_OPTIONS[DESKTOP_COMPONENTS.ADD_TO_CART]
    ];

    if (staticPageId) {
      fragments.push(
        {...DESKTOP_COMPONENTS_OPTIONS[DESKTOP_COMPONENTS.HEADER],
          paramater: {
            ...DESKTOP_COMPONENTS_OPTIONS[DESKTOP_COMPONENTS.HEADER].paramater,
            isBot
          }}
      );
    } else {
      fragments.push({
        ...DESKTOP_COMPONENTS_OPTIONS[DESKTOP_COMPONENTS.OLD_HEADER],
        cacheStatus: context.matchResult.routeName !== PAGES.SEARCH_PAGE && !isBot,
        paramater:{
          ...DESKTOP_COMPONENTS_OPTIONS[DESKTOP_COMPONENTS.OLD_HEADER].paramater,
          [FACET_QUERY_PARAM_NAMES.QUERY]: context?.queryParams?.[FACET_QUERY_PARAM_NAMES.QUERY],
          isBot
        },
      },
      DESKTOP_COMPONENTS_OPTIONS[DESKTOP_COMPONENTS.NAVIGATION_DESKTOP]
      );
    }

    const cityCode = jsonParse(cookies?.location) || null;
    dispatch(filterActionCreators.setDeliveryLocation(cityCode));

    result = await resolvePromiseList(Promise.all([
      resolveWithSettleContext(
        dispatch(
          voltranActionCreators.getFragments.trigger(fragments),
        ),
      ),
      !hasSpecialFooter && resolveWithSettleContext(
        dispatch(
          footerActionCreators.getFooterItems.trigger({
            platform: 'desktop'
          })
        ),
      ),
    ])).then(([getFragmentsResponse]) => {
      if (isExistPartialFragment(getFragmentsResponse?.result)) {
        fetchOperationContext.isPartialContent = true;
      }

      if (getFragmentsResponse?.result?.find(item => !item?.result)) {
        fetchOperationContext.isPartialContent = true
      }

      return fetchOperationContext;
    });

    return result;
  }

  constructor(props) {
    super(props);
    this.state = {
      isHydratingCompleted: false,
      routeName: this.getActiveRouteName(),
      routeChange: false,
      routeProps: this.getActiveRoute(),
      rewriteUrl: this.props.isRewriteUrl
    };
  }


  generateRoutingProps() {
    const {
      staticContext,
      location,
      userAgentState: serverContext,
      appFeatureToggleMap
    } = this.props;
    const {routeChange, routeProps} = this.state;
    const {friendlyUrl, isCollectionPage, url} = serverContext;
    const isHurlyActive = appFeatureToggleMap?.get(FEATURE_TOGGLE_NAMES.ENABLE_FRIENDLY_URLS_FOR_CATEGORY_PAGES);
    const queryParams = extractQueryParamsFromLocation({location, serverContext, isCollectionPage, isHurlyActive});

    return {
      queryParams,
      isMobile: false,
      isBot: staticContext?.isBot,
      platform: PLATFORMS.DESKTOP,
      routeChange,
      routeProps,
      friendlyUrl,
      url,
      isCollectionPage
    };
  }

  getActiveRouteName(location = this.props.location) {
    const {
      rewriteUrl
    } = this.props
    const activeRoute = matchUrlInRouteConfigs(rewriteUrl?.rewriteUrl ? `/${rewriteUrl?.rewriteUrl}` : location.pathname);
    if (activeRoute) {
      return activeRoute.routeName;
    }
    return null;
  }

  getActiveRoute(location = this.props.location) {
    const {
      rewriteUrl
    } = this.props

    const activeRoute = matchUrlInRouteConfigs(rewriteUrl?.rewriteUrl ? `/${rewriteUrl?.rewriteUrl}` : location.pathname);
    if (activeRoute) {
      return activeRoute;
    }
    return null;
  }

  componentDidMount() {
    this.setState({
      isHydratingCompleted: true,
    });

    this.triggerHistoryListener();
    this.triggerLocationListener();
  }

  UNSAFE_componentWillUpdate(nextProps) {
    const {
      location,
      seoInfo,
    } = this.props;

    if (seoInfo !== nextProps.seoInfo && nextProps.seoInfo !== null) {
      utagBootstrapper.init(nextProps.seoInfo);
    }

    const url = location.pathname + location.search;
    const nextUrl = nextProps.location.pathname + nextProps.location.search;

    if (url !== nextUrl && process.env.BROWSER) {
      window.ref = window.location.origin + url;
    }
  }

  triggerHistoryListener = () => {
    const {
      history,
    } = this.props;

    history.listen((location, action) => {
      if (location !== action && action !== 'POP' && location.hash === '') {
        this.setState({
          routeName: this.getActiveRouteName(location),
          routeChange: true,
          routeProps: this.getActiveRoute(location),
        })

        // @todo refactor
        window.routeChange = true;
      }
    })
  }

  triggerLocationListener = () => {
    const {
      dispatch,
    } = this.props;

    const locationCookie = getBrowserCookie(LOCATION_COOKIE_KEY) || null;
    dispatch(filterActionCreators.setDeliveryLocation(jsonParse(locationCookie)));
  }

  render() {
    const {
      location,
      history,
      rewriteUrl,
      isOldHomepage
    } = this.props;

    const {
      routeName,
      routeChange,
      routeProps,
    } = this.state;

    if(rewriteUrl?.rewriteUrl){
      location.pathname = `/${rewriteUrl?.rewriteUrl}`
    }

    return (
      <ReactRenderContext.Provider value={this.state.isHydratingCompleted}>
        <MasterLayout
          history={history}
          routeName={routeName}
          routeChange={routeChange}
          queryParams={this.generateRoutingProps().queryParams}
          location={location}>
          <Switch>
            {
              renderRoutes(this.generateRoutingProps(),isOldHomepage ?? false)
            }
          </Switch>
        </MasterLayout>
      </ReactRenderContext.Provider>
    );
  }
}

function mapStateToProps(state) {
  const {
    featureToggleState: {
      appFeatureToggleMap,
    },
    experimentState: {
      appExperimentMap,
    },
    seoState: {
      seoInfo,
    },
    staticPageState:{
      rewriteUrl
    },
    userAgentState,
    abTestState: {
      isOldHomepage
    }
  } = state;

  return {
    appFeatureToggleMap,
    appExperimentMap,
    seoInfo,
    rewriteUrl,
    userAgentState,
    isOldHomepage
  };
}

const ConnectedAppDesktop = connect(mapStateToProps)(AppDesktop);

export {AppDesktop};
export default hot(module)(withRouter(ConnectedAppDesktop));
